'use client';
import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { 
  ChevronDown, Copy, Check, Plus, 
  ShoppingCart, LayoutDashboard, Settings, LogOut,
  ShieldAlert
} from 'lucide-react';

import { useLanguage } from '@/components/LanguageProvider';

interface AccountMenuProps {
  user: any; 
  logout: () => void;
  showBalance?: boolean;
  onOpenDeposit?: () => void;
  alignDropdownToHeader?: boolean;
}

export default function AccountMenu({ user, logout, showBalance = true, onOpenDeposit, alignDropdownToHeader = false }: AccountMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  const { t } = useLanguage();
  const tm = t.auth.menu;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ВЫЧИСЛЯЕМ ФЕЙКОВЫЙ ID (500000000 + ID пользователя)
  const displayId = user?.id ? 500000000 + user.id : 0;

  const copyPlayerId = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (displayId) {
      navigator.clipboard.writeText(displayId.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatCrystals = (amount: number | undefined) => (amount ?? 0).toLocaleString('ru-RU');

  if (!user) return null;

  return (
    <div className={`relative z-50 flex items-center ${alignDropdownToHeader ? 'h-full' : ''}`} ref={menuRef}>
      {/* Кнопка-триггер профиля */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center gap-2 pl-2 pr-3 py-1.5 border border-white/10 rounded-full cursor-pointer bg-white/5 hover:bg-white/10 backdrop-blur-md transition-all duration-200"
      >
        <Image 
          src={`https://sg-public-api.royolands.com/rpweb/morda/${user.nickname}`} 
          alt="Skin" 
          width={32} 
          height={32} 
          className="rounded-full relative z-10 bg-black/20" 
          unoptimized 
        />
        <ChevronDown size={16} className={`text-white/60 transition-transform duration-300 ${isOpen ? 'rotate-180 text-white' : ''}`} />
      </div>

      {/* Выпадающее меню */}
      <div className={`absolute top-full right-0 mt-3 w-[310px] transition-all duration-300 origin-top-right ${
        isOpen ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
      }`}>
        <div className="bg-[#18181f]/95 backdrop-blur-xl border border-white/10 rounded-3xl p-5 shadow-2xl">
          
          <div className="flex items-center gap-4 mb-4">
            <Image 
              src={`https://sg-public-api.royolands.com/rpweb/morda/${user.nickname}`} 
              alt="Avatar" 
              width={54} 
              height={54} 
              className="rounded-2xl bg-white/5" 
              unoptimized 
            />
            <div className="min-w-0">
              <h4 className="font-semibold text-[16px] text-white truncate">{user.nickname}</h4>
              <button onClick={copyPlayerId} className="flex items-center gap-1.5 text-[13px] text-white/50 hover:text-[#667fff] transition-colors mt-0.5">
                #{displayId} {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          
          <div className="h-[1px] bg-white/10 w-full mb-4" />
          
          {showBalance && (
            <button 
              onClick={() => { setIsOpen(false); onOpenDeposit?.(); }} 
              className="w-full mb-4 p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-transparent hover:border-white/10 transition-all group flex items-center justify-between outline-none cursor-pointer"
            >
              <div className="flex flex-col items-start">
                <span className="text-[11px] uppercase font-semibold text-white/50 tracking-wider">{tm.balance}</span>
                <span className="font-bold text-[20px] text-white mt-0.5">{formatCrystals(user.crystals)} 💎</span>
              </div>
              <div className="w-10 h-10 rounded-full bg-[#667fff] flex items-center justify-center text-white shadow-lg shadow-[#667fff]/20 group-hover:scale-105 transition-transform">
                <Plus size={18} />
              </div>
            </button>
          )}
          
          <div className="space-y-1.5 mb-3">
            {user.hasAccess === false && (
              <a href="/buy/multiverse" target="_blank" rel="noopener noreferrer" className="w-full p-3 rounded-xl bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 transition-all flex items-center gap-3 text-[14px] font-medium cursor-pointer">
                <ShoppingCart size={18} /> {tm.buyAccess}
              </a>
            )} 

            {/* ПАНЕЛЬ УПРАВЛЕНИЯ ДЛЯ АДМИНИСТРАТОРОВ */}
            {user.roles === 'ADMIN' && (
              <Link href="https://admin.royolands.com/" onClick={() => setIsOpen(false)} className="w-full flex items-center gap-3 p-3 rounded-xl text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 transition-colors text-[14px] font-medium cursor-pointer">
                <ShieldAlert size={18} /> {tm.adminPanel}
              </Link>
            )}

            <Link href="/me" onClick={() => setIsOpen(false)} className="w-full flex items-center gap-3 p-3 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors text-[14px] font-medium cursor-pointer">
              <LayoutDashboard size={18} /> {tm.dashboard}
            </Link>
            <a href="https://account.royolands.com/settings" target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-3 p-3 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors text-[14px] font-medium cursor-pointer">
              <Settings size={18} /> {tm.settings}
            </a>
          </div>
          
          <div className="h-[1px] bg-white/10 w-full mb-3" />
          
          <button onClick={() => { setIsOpen(false); logout(); }} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl text-red-400/90 hover:text-red-400 hover:bg-red-400/10 transition-colors text-[14px] font-semibold cursor-pointer outline-none">
            <LogOut size={18} /> {tm.logout}
          </button>
        </div>
      </div>
    </div>
  );
}